import { reportClientError } from './errorHandling';

const DRAFT_KEY = 'b2b-rfq';
const PUBLIC_FIELDS = ['product_id', 'name', 'sku', 'unit', 'supplier_name'];

function publicDraftLine(line) {
  if (!line || !PUBLIC_FIELDS.every(key => typeof line[key] === 'string')) return null;
  if (!Number.isInteger(line.quantity) || !Number.isInteger(line.moq)) return null;
  return { ...Object.fromEntries(PUBLIC_FIELDS.map(key => [key, line[key]])), moq: line.moq, quantity: line.quantity };
}

function parseDraft(serialized) {
  const value = JSON.parse(serialized);
  if (!Array.isArray(value)) return [];
  return value.slice(0, 500).map(publicDraftLine).filter(Boolean);
}

// Legacy reads are migration-only. Never persist drafts or credentials here again.
function readLegacyDraft() {
  try {
    return localStorage.getItem(DRAFT_KEY);
  } catch (error) {
    reportClientError('Legacy product list migration is unavailable', error);
    return null;
  }
}

export function readDraft() {
  try {
    const current = sessionStorage.getItem(DRAFT_KEY);
    const legacy = readLegacyDraft();
    const draft = parseDraft(current ?? legacy ?? '[]');
    // Persist sanitized migration before React StrictMode re-runs initializers.
    // On quota failure retain the legacy copy until provider mount cleanup, so
    // repeated initializers can still recover the same in-memory draft.
    if (saveDraft(draft)) clearLegacySessionStorage();
    return draft;
  } catch (error) {
    reportClientError('Stored product list could not be restored', error);
    return [];
  }
}

export function saveDraft(draft) {
  try {
    const lines = draft.slice(0, 500).map(publicDraftLine).filter(Boolean);
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify(lines));
    return true;
  } catch (error) {
    reportClientError('Product list could not be saved for this tab', error);
    return false;
  }
}

export function clearDraftStorage() {
  try {
    sessionStorage.removeItem(DRAFT_KEY);
    return true;
  } catch (error) {
    reportClientError('Tab product list could not be cleared', error);
    return false;
  }
}

export function clearLegacySessionStorage() {
  try {
    ['token', 'user', DRAFT_KEY].forEach(key => localStorage.removeItem(key));
    ['token', 'user'].forEach(key => sessionStorage.removeItem(key));
    return true;
  } catch (error) {
    reportClientError('Legacy storage cleanup is unavailable', error);
    return false;
  }
}

export function mergeDraftItems(previous, items) {
  const lines = new Map(previous.map(line => [line.product_id, line]));
  items.forEach(({ product, quantity }) => {
    const current = lines.get(product.id);
    lines.set(product.id, {
      product_id: product.id, name: product.name, sku: product.sku, unit: product.unit,
      moq: product.moq, supplier_name: product.supplier_name,
      quantity: (current?.quantity || 0) + quantity,
    });
  });
  return Array.from(lines.values());
}